/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a depot.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDepotState {
    depot: Dtos.Depot | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDepotState {
    depot: IInitialDepotState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DepotInitialState: IInitialDepotState = {
    depot: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDepotSelector: TypedUseSelectorHook<IDepotState> = useSelector;

export const DepotSelectors = {
    selectDepot: (state: IDepotState) => state.depot.depot,
    selectLoadState: (state: IDepotState) => state.depot.loadState,
    selectSaveState: (state: IDepotState) => state.depot.saveState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DepotReducer extends ImmerReducer<IInitialDepotState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(depot?: Dtos.Depot) {
        this.draftState.depot = depot ? depot : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.depot = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.Depot) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(depot?: Dtos.Depot) {
        this.draftState.depot = depot ? depot : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.depot = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DepotInitialState };
    }
};

export const DepotActions = createActionCreators(DepotReducer);
const reducer = createReducerFunction(DepotReducer, DepotInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DepotApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetDepotById({
            id
        }));
    },
    save: (depot?: Dtos.Depot) => {
        return client.post(new Dtos.SetDepot({
            depot
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DepotLogic = {
    loadById: createLogic<IDepotState, {}, undefined, string, ReturnType<typeof DepotActions.loadById>>({
        type: DepotActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await DepotApi.loadById(id);

                dispatch(DepotActions.loadSuccess(
                    response.depot
                ));
            }
            catch (error: any) {
                dispatch(DepotActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IDepotState, {}, undefined, string, ReturnType<typeof DepotActions.save>>({
        type: DepotActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const depot = action.payload;

            try {
                const response = await DepotApi.save(depot);

                dispatch(DepotActions.saveSuccess(
                    response.depot
                ));
            }
            catch (error: any) {
                dispatch(DepotActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DepotLogic.loadById,
    DepotLogic.save
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDepot = (): [Dtos.Depot | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof DepotActions] => {
    const depot = useSelector(DepotSelectors.selectDepot);
    const loadState = useSelector(DepotSelectors.selectLoadState);
    const saveState = useSelector(DepotSelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DepotActions>(() => {
        return bindActionCreators(DepotActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DepotActions, dispatch));
    }, [dispatch, setActions]);

    return [
        depot,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('depot', reducer as any, logics as any);